import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { formatDateTime } from '@/lib/format'
import type { AlertSummary } from '@/features/alerts/types'

/**
 * Horizontal "Fraud Sequence Timeline" — the catalogue mock shows
 * 3-5 evidence dots strung along a timeline (PIN reset → Mobile
 * txn → Agency alert → Decision pending). We project linked alerts
 * onto the same shape: each alert becomes a dot with the scenario
 * name + amount + relative timestamp.
 *
 * The timeline orders alerts oldest→newest (left to right) so the
 * fraud progression reads in causal order.
 */
export function FraudSequenceTimeline({ alerts }: { alerts: AlertSummary[] }) {
  if (alerts.length === 0) {
    return null
  }
  const sorted = [...alerts].sort(
    (a, b) =>
      new Date((a as unknown as { created_at: string }).created_at).getTime() -
      new Date((b as unknown as { created_at: string }).created_at).getTime(),
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fraud sequence timeline</CardTitle>
        <p className="text-xs text-muted-fg">
          The order in which the linked alerts fired. Oldest signal on the left.
        </p>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-wrap items-stretch gap-2">
          {sorted.map((a, i) => (
            <li key={a.id} className="flex items-center gap-2">
              <TimelineDot alert={a} index={i} />
              {i < sorted.length - 1 ? (
                <ArrowRight size={14} className="shrink-0 text-muted-fg" aria-hidden />
              ) : null}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}

function TimelineDot({ alert, index }: { alert: AlertSummary; index: number }) {
  const a = alert as unknown as {
    id: string
    risk_score?: number
    priority?: string
    alert_type?: string
    scenario_name?: string
    created_at: string
    transaction?: { amount_minor?: number; channel?: string }
  }
  const score = a.risk_score ?? 0
  const tone =
    score >= 80
      ? 'border-status-failed/40 bg-status-failed/10'
      : score >= 50
        ? 'border-status-pending/40 bg-status-pending/10'
        : 'border-border bg-muted'
  const channel = a.transaction?.channel ?? ''
  const amount = a.transaction?.amount_minor != null ? a.transaction.amount_minor : null

  return (
    <Link
      to={`/alerts/${a.id}`}
      className={
        'block min-w-[10rem] max-w-[14rem] rounded-md border-l-4 px-3 py-2 transition-colors hover:bg-card-hover ' +
        tone
      }
      title={a.scenario_name || a.alert_type}
    >
      <div className="flex items-baseline justify-between gap-1">
        <span className="text-[0.625rem] uppercase tracking-wider text-muted-fg">
          Step {index + 1}
        </span>
        <Badge
          variant={
            score >= 80 ? 'failed' : score >= 50 ? 'pending' : 'info'
          }
          className="text-[0.625rem]"
        >
          {score}
        </Badge>
      </div>
      <p className="mt-1 truncate text-xs font-semibold">
        {a.scenario_name || a.alert_type || 'Alert'}
      </p>
      {channel ? (
        <p className="mt-0.5 truncate text-[0.625rem] uppercase tracking-wider text-muted-fg">
          {channel.replace(/_/g, ' ')}
        </p>
      ) : null}
      {amount != null ? (
        <p className="mt-0.5 truncate text-[0.6875rem] font-medium tabular-nums">
          ₦{(amount / 100).toLocaleString('en-NG', { maximumFractionDigits: 0 })}
        </p>
      ) : null}
      <p className="mt-1 truncate text-[0.625rem] text-muted-fg">
        {formatDateTime(a.created_at, '—')}
      </p>
    </Link>
  )
}
